/**
 * Express printer non-printable margins.
 * Stored on the express printer as properties.margin { l, r, t, b } in mm;
 * relevant only for non-pics printers that define all 4 sides.
 */

export interface PrinterMarginCM {
  l: number;
  r: number;
  t: number;
  b: number;
}

/** Express printer document (not a cluster) -> margins in cm, or null */
export function getExpressPrinterMarginCM(expressPrinter: any): PrinterMarginCM | null {
  if (!expressPrinter || expressPrinter.isPicsPrinter) {
    return null;
  }
  const margin = expressPrinter.properties?.margin;
  if (!margin) {
    return null;
  }
  const sides = ['l', 'r', 't', 'b'].map((side) => {
    const value = margin[side];
    if (value === null || value === undefined || value === '') {
      return NaN;
    }
    return Number(value?.$numberDouble ?? value);
  });
  if (sides.some((value) => !Number.isFinite(value) || value < 0)) {
    return null;
  }
  const [l, r, t, b] = sides.map((mm) => mm / 10);
  return { l, r, t, b };
}

/** Paper area left after removing the margins (cm) */
export function getPrintableArea(
  paperWidth: number,
  paperHeight: number,
  margin: PrinterMarginCM | null
): { width: number; height: number } {
  if (!margin) {
    return { width: paperWidth, height: paperHeight };
  }
  return {
    width: paperWidth - margin.l - margin.r,
    height: paperHeight - margin.t - margin.b,
  };
}

/** Image gaps (cm) from each paper edge; with margins the image is centered in the printable area */
export function getImageGapsCM(
  paperWidth: number,
  paperHeight: number,
  imageWidth: number,
  imageHeight: number,
  margin: PrinterMarginCM | null
): { left: number; right: number; top: number; bottom: number } {
  const area = getPrintableArea(paperWidth, paperHeight, margin);
  const left = (margin?.l ?? 0) + (area.width - imageWidth) / 2;
  const top = (margin?.t ?? 0) + (area.height - imageHeight) / 2;
  return {
    left,
    right: paperWidth - imageWidth - left,
    top,
    bottom: paperHeight - imageHeight - top,
  };
}
